import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  apiClient,
  getEventsPageViaWebSocket,
  subscribeEventsIndexed,
  subscribeNewEvent,
} from "../../api/client";
import {
  Download,
  Search,
  Calendar,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  MessageSquare,
  Settings2,
  Send,
  Trash2,
} from "lucide-react";

/** IST for event capture_time: ISO `...Z` is UTC; date+time must use the same zone (matches API dateTime). */
const EVENT_DISPLAY_TIMEZONE = "Asia/Kolkata";

function toDateString(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function formatDate(value: string): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: EVENT_DISPLAY_TIMEZONE,
  });
}
function formatTime(value: string): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: EVENT_DISPLAY_TIMEZONE,
  });
}
function defaultDateFrom(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return toDateString(d);
}
function defaultDateTo(): string {
  return toDateString(new Date());
}

function truncateWords(input: string, maxWords: number): string {
  const text = (input ?? "").trim();
  if (!text) return "";
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(" ")}…`;
}

type EventStatus = "UNRESOLVED" | "RESOLVED" | "REJECT";

type EventRow = {
  id: string;
  capture_time: string;
  category: string;
  subcategory: string;
  status: EventStatus;
  sensor_name: string;
  location: string;
  remarks: string;
  labelled_image_link?: string;
  video_link?: string;
  lastClientRemark?: {
    message?: string;
    createdAt?: string;
    clientName?: string;
    clientId?: string;
  };
};

function normalizeEventStatus(status: unknown): EventStatus {
  const value = String(status ?? "").trim().toUpperCase();
  if (value === "RESOLVED" || value === "RESOLVE") return "RESOLVED";
  if (value === "REJECT" || value === "REJECTED") return "REJECT";
  return "UNRESOLVED";
}

function mapApiEventToRow(e: any): EventRow {
  const status = e.resolution_status ?? e.status ?? "Unresolved";
  const category = e.category?.trim() || "Event";
  const subcategory = e.subcategory?.trim() || "";
  return {
    id: e.id,
    capture_time: e.capture_time ?? e.captureTime ?? "",
    category,
    subcategory,
    status: normalizeEventStatus(status),
    sensor_name: (e.sensor_name ?? e.sensorName)?.trim() || "—",
    location: e.location ?? e.location_name ?? e.locationName ?? "—",
    remarks: e.remarks ?? e.controller_remark ?? "",
    labelled_image_link: e.labelled_image_link ?? e.image_link ?? e.unlabelledImgLink ?? "",
    video_link: e.video_link ?? e.trimVideoLink ?? "",
    lastClientRemark: e.lastClientRemark
      ? {
        message: e.lastClientRemark?.message,
        createdAt: e.lastClientRemark?.createdAt,
        clientName: e.lastClientRemark?.clientName,
        clientId: e.lastClientRemark?.clientId,
      }
      : undefined,
  };
}

export const Events: React.FC = () => {
  const chatRef = useRef<HTMLDivElement | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [dateFrom, setDateFrom] = useState(() => defaultDateFrom());
  const [dateTo, setDateTo] = useState(() => defaultDateTo());
  const [statusFilter, setStatusFilter] = useState("All");
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  /** Values last sent to the API (updated via Apply or Clear). */
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => defaultDateFrom());
  const [appliedDateTo, setAppliedDateTo] = useState(() => defaultDateTo());
  const [appliedStatus, setAppliedStatus] = useState("All");
  const [appliedSubcategory, setAppliedSubcategory] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [events, setEvents] = useState<EventRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewEvent, setPreviewEvent] = useState<EventRow | null>(null);
  const [previewVideoEvent, setPreviewVideoEvent] = useState<EventRow | null>(null);
  const [previewImageError, setPreviewImageError] = useState(false);
  const [deleteConfirmEvent, setDeleteConfirmEvent] = useState<EventRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [isSelectedId, setIsSelectedId] = useState<string | null>(null);
  // const [remarkData, setRemarkData] = useState<RemarkDataType | null>(null);
  // const [remarkData, setRemarkData] = useState<Record<string, unknown[]>>({});
  // const [remarkData, setRemarkData] = useState([]);
  const [remarkData, setRemarkData] = useState<Record<string, any[]>>({});
  const authUser = JSON.parse(localStorage.getItem("auth_user") || "{}");
  const isAdmin = Array.isArray(authUser?.roles) && authUser.roles.includes("ADMIN");

  const eventsInFlightRef = useRef(false);
  const hasInitialEventsLoadedRef = useRef(false);


  const fetchEvents = useCallback(async () => {
    if (eventsInFlightRef.current) return;
    eventsInFlightRef.current = true;

    const shouldSetLoading = !hasInitialEventsLoadedRef.current;
    if (shouldSetLoading) setLoading(true);
    setError(null);

    let didSucceed = false;

    /** Calendar dates from <input type="date" /> are IST calendar days; send bounds with +05:30 (IST). */
    const parseYmdToIstIso = (ymd: string | undefined, endOfDay: boolean) => {
      if (!ymd) return undefined;
      const parts = ymd.split("-").map((v) => Number(v));
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return undefined;
      const [y, m, d] = parts;
      const yyyy = String(y);
      const mm = String(m).padStart(2, "0");
      const dd = String(d).padStart(2, "0");
      return endOfDay
        ? `${yyyy}-${mm}-${dd}T23:59:59.999+05:30`
        : `${yyyy}-${mm}-${dd}T00:00:00.000+05:30`;
    };

    try {
      const getClientDetails = JSON.parse(localStorage.getItem("auth_user") || "{}");
      const roles = Array.isArray(getClientDetails?.roles)
        ? getClientDetails.roles
        : getClientDetails?.role
          ? [getClientDetails.role]
          : [];
      const isViewer = roles.some((r: any) => String(r).toUpperCase() === "VIEWER");
      const creatorId = String(
        getClientDetails?.creator_id ??
        getClientDetails?.creatorId ??
        "",
      ).trim();
      const withClientDetails = {
        role: roles,
        creatorId: creatorId || (isViewer ? String(getClientDetails?.id ?? "") : ""),
        id: String(getClientDetails?.id ?? ""),
      };

      const params: Record<string, string | number | boolean | undefined> = {
        page,
        pageSize,
        signed: true,
        from: parseYmdToIstIso(appliedDateFrom, false),
        to: parseYmdToIstIso(appliedDateTo, true),
      };
      if (appliedStatus !== "All") params.resolution_status = appliedStatus;
      if (appliedSubcategory) params.subcategory = appliedSubcategory;
      if (appliedSearch) params.search = appliedSearch;

      // Use websocket-backed request (no REST GET needed).
      const res = await getEventsPageViaWebSocket<any>({
        ...params,
        ...withClientDetails,
      });

      const payload = res ?? {};
      const list =
        Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.response)
              ? payload.response
              : Array.isArray(payload?.response?.data)
                ? payload.response.data
                : Array.isArray(payload?.items)
                  ? payload.items
                  : Array.isArray(payload?.events_data?.data)
                    ? payload.events_data.data
                    : [];

      const paginationTotalCount =
        payload?.pagination?.totalCount ??
        payload?.pagination?.total_count ??
        payload?.totalCount ??
        payload?.total_count ??
        list.length;

      const rows = list.map(mapApiEventToRow);
      setEvents(rows);
      setTotalCount(paginationTotalCount);
      didSucceed = true;
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load events");
      setEvents([]);
      setTotalCount(0);
    } finally {
      if (shouldSetLoading) setLoading(false);
      if (didSucceed) hasInitialEventsLoadedRef.current = true;
      eventsInFlightRef.current = false;
    }
  }, [appliedDateFrom, appliedDateTo, appliedStatus, appliedSubcategory, appliedSearch, page, pageSize]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Realtime: gateway pushes `events_indexed` over `/surveillance-overview` when ES indexes an event (Redis).
  useEffect(() => {
    const unsub = subscribeEventsIndexed(() => {
      fetchEvents();
    });

    return () => {
      unsub();
    };
  }, [fetchEvents]);

  useEffect(() => {
    const unsub = subscribeNewEvent((incoming) => {
      const row = mapApiEventToRow(incoming);
      if (!row?.id) return;

      setTotalCount((count) => count + 1);

      setEvents((prev) => {
        if (prev.some((event) => event.id === row.id)) return prev;
        if (page !== 1) return prev;
        return [row, ...prev].slice(0, pageSize);
      });
    });

    return () => {
      unsub();
    };
  }, [page, pageSize]);

  useEffect(() => {
    if (previewEvent) setPreviewImageError(false);
  }, [previewEvent]);

  const filtered = events;


  // Reserved for delete row button (currently commented out in JSX)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteClick = useCallback((event: EventRow) => {
    setDeleteConfirmEvent(event);
  }, []);

  const handleDeleteConfirm = async () => {
    const event = deleteConfirmEvent;
    if (!event?.id) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/surveillance/event/${event.id}`);
      setEvents((prev) => prev.filter((e) => e.id !== event.id));
      setTotalCount((c) => Math.max(0, c - 1));
      setError(null);
      setDeleteConfirmEvent(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.response?.statusText ?? err?.message ?? "Delete failed";
      setError(msg);
      setTimeout(() => setError(null), 5000);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!deleting) setDeleteConfirmEvent(null);
  };

  const handleUpdateStatus = async (id: string, status: EventStatus) => {
    if (!id || !status) return;
    try {
      await apiClient.put(`/surveillance/event/${id}`, {
        status,
        remark: status === "RESOLVED" ? "Issue resolved by QC" : "",
        is_fixed_by_quality_member: status === "RESOLVED",
      });
      setEvents((prev) =>
        prev.map((e) => (e.id === id ? { ...e, status } : e))
      );
    } catch (_) {
      // swallow for now; you may want to show an error toast
    }
  };

  const handleSaveRemark = async (id: string, value: string) => {
    const message = value?.trim();
    if (!message) return;

    const authUser = JSON.parse(localStorage.getItem("auth_user") || "{}");
    const clientId = authUser?.id;
    const clientName = authUser?.full_name || authUser?.email || "Unknown";

    try {
      await apiClient.patch(`/surveillance/client-remarks`, {
        eventId: id,
        clientId,
        clientName,
        message,
      });
      setRemarks((prev) => ({
        ...prev,
        [id]: "",
      }));
      await fetchRemarksByEventId(id);
    } catch (err) {
      console.log("Send failed");
    }
  };
  // const sortedRemarks = [...(remarkData?.clientRemarks || [])].sort(
  //   (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  // );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sortedRemarks = isSelectedId
    ? [...(remarkData[isSelectedId] || [])].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )
    : [];
  // console.log("sort remark is : ",sortedRemarks)
  const isDefaultDateRange =
    appliedDateFrom === defaultDateFrom() && appliedDateTo === defaultDateTo();

  const handleApplyFilters = () => {
    const trimmed = searchQuery.trim();
    setPage(1);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setAppliedStatus(statusFilter);
    setAppliedSubcategory(subcategoryFilter);
    setAppliedSearch(trimmed.length >= 3 ? trimmed : "");
  };

  const nextSearchApplied = searchQuery.trim().length >= 3 ? searchQuery.trim() : "";
  const filtersDirty =
    dateFrom !== appliedDateFrom ||
    dateTo !== appliedDateTo ||
    statusFilter !== appliedStatus ||
    subcategoryFilter !== appliedSubcategory ||
    nextSearchApplied !== appliedSearch;

  const handleClear = () => {
    const from = defaultDateFrom();
    const to = defaultDateTo();
    setPage(1);
    setSearchQuery("");
    setStatusFilter("All");
    setSubcategoryFilter("");
    setDateFrom(from);
    setDateTo(to);
    setAppliedSearch("");
    setAppliedStatus("All");
    setAppliedSubcategory("");
    setAppliedDateFrom(from);
    setAppliedDateTo(to);
  };

  const handleExport = () => {
    const header = "Date & Time,Category,Status,Camera,Location,Remarks";
    const rows = filtered.map((e) => {
      const when = e.capture_time
        ? `${formatDate(e.capture_time)} ${formatTime(e.capture_time)}`
        : "—";
      return `"${when}","${e.subcategory || e.category}","${e.status}","${e.location}","${remarks[e.id] ?? ""}"`;
    });
    const blob = new Blob([header + "\n" + rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "events.csv"; a.click();
  };

  const filtersActive =
    appliedStatus !== "All" ||
    appliedSearch.length >= 3 ||
    appliedSubcategory.trim().length > 0 ||
    !isDefaultDateRange;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize || 1));


  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [remarkData, isSelectedId]);


  const fetchRemarksByEventId = async (eventId: string) => {
    try {
      const res = await apiClient.get(`/surveillance/client-remarks/${eventId}`);

      const data = res?.data?.data || res?.data || [];

      // 🔥 IMPORTANT: store per eventId
      setRemarkData((prev) => ({
        ...prev,
        [eventId]: data || [],
      }));
    } catch (err) {
      console.log("Failed to fetch remarks");
    }
  };

  const handleOpenComments = async (eventId: string) => {
    setIsSelectedId(eventId);
    setRemarkData((prev) => ({
      ...prev,
      [eventId]: [],
    }));
    await fetchRemarksByEventId(eventId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-2xl">
            Monitor and manage security events
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            • Default date range (1 year) •{" "}
            {loading
              ? "Loading…"
              : `${filtered.length} of ${totalCount} events · Page ${page} of ${totalPages}`}
          </p>
          {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
        <button
          onClick={handleExport}
          className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-100 px-4 py-2.5 text-sm font-medium text-blue-700 transition-colors hover:border-blue-500/40 hover:bg-slate-200 sm:mt-0 dark:border-[#334155] dark:bg-[#1C202F] dark:text-blue-300 dark:hover:bg-[#1C202F]/70"
        >
          <Download className="h-4 w-4" />Export to CSV
        </button>
      </div>

      {/* Filters bar */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-[#1C202F]/45 dark:shadow-none">
        <div className="grid grid-cols-1 gap-6 p-4 md:grid-cols-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Camera & Location Search</span>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleApplyFilters();
                  }
                }}
                placeholder="Search camera & location"
                className="h-10 w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-500 focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-100 dark:placeholder:text-slate-400"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Date Range</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-10 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 [color-scheme:light] dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-100 dark:[color-scheme:dark]"
              />
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-10 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 [color-scheme:light] dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-100 dark:[color-scheme:dark]"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Category or sub-category</span>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                value={subcategoryFilter}
                onChange={(e) => setSubcategoryFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleApplyFilters();
                  }
                }}
                placeholder="Search category or sub-category"
                className="h-10 w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-500 focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-100 dark:placeholder:text-slate-400"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Status</span>
            </div>
            <div className="flex gap-2">
              <div className="relative min-w-0 flex-1">
                <Filter className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-10 w-full appearance-none rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-10 text-sm text-slate-800 focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/25 dark:border-[#334155] dark:bg-[#0B0F19]/30 dark:text-slate-200"
                >
                  <option value="All">All</option>
                  <option value="UNRESOLVED">Unresolved</option>
                  <option value="RESOLVED">Resolved</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              </div>
              <button
                type="button"
                onClick={handleApplyFilters}
                disabled={!filtersDirty}
                className="h-10 shrink-0 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-500"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Active filters summary */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700/80 dark:bg-[#1C202F]/30">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-500" />
          <div>
            <p className="text-sm text-slate-700 dark:text-slate-300">
              Date range: {appliedDateFrom} — {appliedDateTo}
            </p>
            <p className="text-xs text-slate-500">
              {filtersActive ? `Filters active · ${filtered.length} results` : "No active search"}
            </p>
          </div>
        </div>
        <button onClick={handleClear} className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${filtersActive ? "bg-red-500/90 hover:bg-red-500" : "cursor-not-allowed bg-slate-300 opacity-60 dark:bg-slate-700"}`} disabled={!filtersActive}>
          Clear Filters
        </button>
      </div>


      {/* chat section  */}
      {isSelectedId && (
        <div className="fixed bottom-6 right-6 z-50 flex w-[320px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">

          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">Conversation</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Event Chat</p>
            </div>

            <button
              onClick={() => setIsSelectedId(null)}
              className="text-lg text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"
            >
              ✕
            </button>
          </div>

          {/* Chat body */}
          <div
            ref={chatRef}
            className="h-64 space-y-3 overflow-y-auto p-3"
          >
            {(!remarkData[isSelectedId] || remarkData[isSelectedId].length === 0) ? (
              <p className="mt-6 text-center text-xs text-slate-500">
                No messages yet
              </p>
            ) : (
              (remarkData[isSelectedId] || []).map((msg) => {
                const currentUserId =
                  localStorage.getItem("auth_user_id") ||
                  JSON.parse(localStorage.getItem("auth_user") || "{}")?.id;
                const isMine =
                  (msg.clientId || msg.client?.id) === currentUserId;

                return (
                  <div
                    key={msg.id}
                    className={`flex ${isMine ? "justify-end" : "justify-start"
                      }`}
                  >
                    <div className="max-w-[75%] flex flex-col gap-1">

                      {/* Name */}

                      <span className="text-[10px] text-green-400">
                        {msg.clientName || msg.client?.fullName || "User"}
                      </span>


                      {/* Bubble */}
                      <div
                        className={`rounded-xl px-3 py-2 text-xs break-words ${isMine
                          ? "rounded-br-sm bg-blue-500 text-white"
                          : "rounded-bl-sm bg-slate-200 text-slate-900 dark:bg-slate-800 dark:text-white"
                          }`}
                      >
                        {msg.message}
                      </div>

                      {/* Time */}
                      <span className="text-[10px] text-slate-500 text-right">
                        {new Date(msg.createdAt).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Input */}
          <div className="relative border-t border-slate-200 p-2 dark:border-slate-700">
            <input
              type="text"
              value={remarks[isSelectedId] ?? ""}
              onChange={(e) =>
                setRemarks((prev) => ({
                  ...prev,
                  [isSelectedId]: e.target.value,
                }))
              }
              placeholder="Write a message..."
              className="w-full rounded-full border border-slate-300 bg-slate-100 py-2 pl-3 pr-10 text-xs text-slate-900 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
            />

            <button
              onClick={() =>
                handleSaveRemark(
                  isSelectedId,
                  remarks[isSelectedId] ?? ""
                )
              }
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-blue-500 p-1.5 rounded-full text-white hover:bg-blue-600 transition"
            >
              <Send className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {/* Events table */}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-700/80 dark:bg-[#1C202F]/45">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-100 dark:border-slate-700/80 dark:bg-[#1C202F]/55">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                Date &amp; Time
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                Event Category
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                Camera
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                Location
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                Image / Video
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                Remarks
              </th>
              {isAdmin && (
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                  <Settings2 className="mx-auto h-4 w-4" />
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700/60">
            {loading ? (
              <tr>
                <td
                  colSpan={isAdmin ? 8 : 7}
                  className="px-4 py-8 text-center text-sm text-slate-600 dark:text-slate-500"
                >
                  Loading events…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={isAdmin ? 8 : 7}
                  className="px-4 py-8 text-center text-sm text-slate-600 dark:text-slate-500"
                >
                  No events match your filters.
                </td>
              </tr>
            ) : (
              filtered.map((event, index) => (
                <React.Fragment key={event.id ?? `event-${index}`}>
                  <tr className="bg-white transition-colors hover:bg-slate-50 dark:bg-[#1C202F]/20 dark:hover:bg-[#1C202F]/35">
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                      {event.capture_time ? (
                        <div className="flex flex-col leading-tight">
                          <span>{formatDate(event.capture_time)}</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">{formatTime(event.capture_time)}</span>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-800 dark:text-slate-200">
                      <div className="flex flex-col leading-tight">
                        <span>{event.category || "Event"}</span>
                        {event.subcategory && (
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {event.subcategory}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-center gap-1">
                        <div className="relative inline-flex items-center">
                          <select
                            value={event.status}
                            onChange={(e) =>
                              handleUpdateStatus(event.id, e.target.value as EventStatus)
                            }
                            className={`cursor-pointer appearance-none rounded-lg border py-1 pl-3 pr-7 text-xs font-semibold transition-colors ${event.status === "UNRESOLVED"
                              ? "border-red-500/25 bg-red-500/15 text-red-700 hover:bg-red-500/25 dark:text-red-300"
                              : event.status === "RESOLVED"
                                ? "border-emerald-500/25 bg-emerald-500/15 text-emerald-800 hover:bg-emerald-500/25 dark:text-emerald-300"
                                : "border-yellow-500/25 bg-yellow-500/15 text-yellow-800 hover:bg-yellow-500/25 dark:text-yellow-200"
                              }`}
                          >
                            <option value="UNRESOLVED">UNRESOLVED</option>
                            <option value="RESOLVED">RESOLVED</option>
                            <option value="REJECT">REJECT</option>
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-1.5 h-3 w-3 text-slate-600 dark:text-slate-300" />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                      {event.sensor_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                      {event.location}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => setPreviewEvent(event)}
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-slate-100 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-slate-200 dark:border-[#334155] dark:bg-[#1C202F]/35 dark:text-blue-300 dark:hover:bg-[#1C202F]/55"
                        >
                          <ImageIcon className="h-3.5 w-3.5" />
                          Image
                        </button>
                        {event.video_link && (
                          <button
                            type="button"
                            onClick={() => setPreviewVideoEvent(event)}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-500/25 bg-emerald-500/15 px-2.5 py-1.5 text-xs font-medium text-emerald-800 transition-colors hover:bg-emerald-500/25 dark:text-emerald-300"
                          >
                            Video
                          </button>
                        )}
                      </div>
                    </td>
                    {/* <td className="px-4 py-3 min-w-[200px]">
                     
                     { isSelectedId === event.id  && (<div className="w-full h-32 overflow-y-auto">
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                          {messages.map((msg, index) => (
                            <div
                              key={msg.id}
                              className="bg-gray-500 p-3 rounded-lg shadow max-w-xs"
                            >
                              <p className={`text-sm font-semibold text-green-500 }`}>
                                {msg.name}
                              </p>
                              <p className="text-gray-100 text-sm">{msg.message}</p>
                            </div>
                          ))}
                        </div>
                      </div>)}
                      <div className="relative">
                        <MessageSquare className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
                        <input
                          type="text"
                          value={remarks[event.id] ?? ""}
                          onChange={(e) =>
                            setRemarks((prev) => ({
                              ...prev,
                              [event.id]: e.target.value,
                            }))
                          }
                          placeholder="Add a comment"
                          className="w-full rounded-lg border border-slate-600 bg-slate-800/60 py-1.5 pl-8 pr-8 text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() =>{
                            handleSaveRemark(event.id, remarks[event.id] ?? "")
                           setIsSelectedId(pre=>pre===event.id?null:event.id)}
                          }
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-blue-500/30 p-1 text-blue-400 hover:bg-blue-500/50"
                          title="Send comment"
                        >
                          <Send className="h-3 w-3" />
                        </button>
                      </div>
                    </td> */}
                    <td className="px-4 py-3 min-w-[180px] text-center">
                      {event.lastClientRemark?.message ? (
                        <div className="mb-1 text-center">
                          <button
                            type="button"
                            onClick={() => handleOpenComments(event.id)}
                            className="inline-flex w-full max-w-[230px] flex-col gap-1 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 px-3 py-2 text-left shadow-sm transition hover:border-blue-500/40 hover:from-slate-100 hover:to-slate-200 dark:border-slate-700/70 dark:from-slate-900/70 dark:to-slate-800/50 dark:hover:border-blue-500/60 dark:hover:from-slate-900/90 dark:hover:to-slate-800/70"
                            title={event.lastClientRemark.message}
                          >
                            <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400">
                              <MessageSquare className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                              <span className="truncate">
                                {event.lastClientRemark.clientName
                                  ? event.lastClientRemark.clientName
                                  : "Last comment"}
                                {event.lastClientRemark.createdAt
                                  ? ` · ${formatDate(
                                    event.lastClientRemark.createdAt
                                  )} ${formatTime(event.lastClientRemark.createdAt)}`
                                  : ""}
                              </span>
                            </span>
                            <span className="text-xs font-medium leading-4 text-slate-800 dark:text-slate-100">
                              {truncateWords(event.lastClientRemark.message, 13)}
                            </span>
                          </button>
                        </div>
                      ) : null}
                      {!event.lastClientRemark?.message ? (
                        <button
                          onClick={() => handleOpenComments(event.id)}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 transition hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <MessageSquare className="h-4 w-4" />
                          Open Comments
                        </button>
                      ) : null}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(event)}
                          className="inline-flex items-center justify-center rounded-lg p-2 text-red-500 transition-colors hover:bg-red-500/10 hover:text-red-600 dark:text-red-400 dark:hover:bg-red-500/20"
                          title="Delete event"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                  {event.remarks &&
                    !["NA", "N/A", "NULL", "NONE"].includes(String(event.remarks).trim().toUpperCase()) && (
                      <tr className="border-none bg-slate-100 dark:bg-slate-900/80">
                        <td
                          colSpan={isAdmin ? 8 : 7}
                          className="px-4 pb-3 pt-1 text-xs text-slate-700 dark:text-slate-300"
                        >
                          <span>Description:</span> {event.remarks}
                        </td>
                      </tr>
                    )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>





      {/* Pagination */}
      <div className="flex items-center justify-between gap-3 text-xs text-slate-600 dark:text-slate-400">
        <div>
          Showing <span className="font-semibold text-slate-900 dark:text-slate-200">{filtered.length}</span> of{" "}
          <span className="font-semibold text-slate-900 dark:text-slate-200">{totalCount}</span> events
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#334155] dark:bg-[#1C202F]/55 dark:text-slate-200 dark:hover:bg-[#1C202F]/70"
          >
            <ChevronLeft className="h-3 w-3" />
            Prev
          </button>
          <span className="min-w-[80px] text-center">
            Page <span className="font-semibold text-slate-900 dark:text-slate-200">{page}</span> of{" "}
            <span className="font-semibold text-slate-900 dark:text-slate-200">{totalPages}</span>
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#334155] dark:bg-[#1C202F]/55 dark:text-slate-200 dark:hover:bg-[#1C202F]/70"
          >
            Next
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmEvent && (
        <div
          onClick={handleDeleteCancel}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-6"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[420px] rounded-[20px] border border-slate-200 bg-white p-6 shadow-2xl dark:border-[#334155] dark:bg-[#1C202F]"
          >
            <div className="mb-5 flex items-start gap-3">
              <div className="rounded-xl bg-red-500/20 p-2.5">
                <Trash2 className="h-6 w-6 text-red-500 dark:text-red-400" />
              </div>
              <div>
                <h3 className="m-0 text-lg font-bold text-slate-900 dark:text-slate-100">Delete event?</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  This will permanently remove <strong className="text-slate-900 dark:text-slate-200">{deleteConfirmEvent.category}</strong> from {deleteConfirmEvent.location}. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleDeleteCancel}
                disabled={deleting}
                className="rounded-lg bg-slate-200 px-4 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-700/80 dark:text-slate-300 dark:hover:bg-slate-600/80"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewEvent && (
        <div onClick={() => setPreviewEvent(null)} className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-6">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded-[20px] border border-slate-200 bg-white p-6 shadow-2xl dark:border-[#334155] dark:bg-[#0f172a]"
            style={{ maxWidth: previewEvent?.labelled_image_link && !previewImageError ? 900 : 480 }}
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="m-0 text-[15px] font-bold text-slate-900 dark:text-slate-100">{previewEvent.category}</h3>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-500">
                  {previewEvent.location}
                  {previewEvent.capture_time
                    ? ` · ${formatDate(previewEvent.capture_time)} ${formatTime(previewEvent.capture_time)}`
                    : ""}
                </p>
              </div>
              {/* <button
                type="button"
                onClick={() => setPreviewEvent(null)}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-slate-200 text-lg text-slate-600 hover:bg-slate-300 dark:bg-slate-700/50 dark:text-slate-400 dark:hover:bg-slate-600/50"
              >
                ×
              </button> */}
            </div>
            <div className="flex min-h-[200px] items-center justify-center overflow-hidden  border border-slate-200 bg-slate-100 dark:border-[#334155] dark:bg-slate-800/80">
              {previewEvent.labelled_image_link && !previewImageError ? (
                <img
                  src={previewEvent.labelled_image_link}
                  alt={`${previewEvent.category} at ${previewEvent.location}`}
                  className="max-h-[70vh] w-auto max-w-full object-contain"
                  onError={() => setPreviewImageError(true)}
                />
              ) : (
                <div className="text-center text-slate-500 dark:text-slate-500">
                  <ImageIcon className="mx-auto mb-2 h-10 w-10" />
                  <p className="text-[13px]">Camera snapshot unavailable</p>
                  <p className="mt-1 text-[11px]">{previewEvent.location} feed</p>
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-wrap justify-end  gap-2.5">
              {previewEvent.labelled_image_link && (
                <a
                  href={previewEvent.labelled_image_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-blue-500/15 px-4 py-2 text-sm font-medium text-blue-700 no-underline transition-colors hover:bg-blue-500/25 dark:bg-blue-500/20 dark:text-blue-400 dark:hover:bg-blue-500/30 flex-1"
                >
                  <ImageIcon className="h-4 w-4" /> Open image in new tab
                </a>
              )}
              <button
                type="button"
                onClick={() => setPreviewEvent(null)}
                className="min-w-[80px] flex  justify-center items-center cursor-pointer rounded-[10px] border border-slate-300 bg-slate-100 py-2 text-[13px] text-slate-700 hover:bg-slate-200 dark:border-[#334155] dark:bg-slate-800/80 dark:text-slate-400 dark:hover:bg-slate-700/80"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {previewVideoEvent && (
        <div
          onClick={() => setPreviewVideoEvent(null)}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-6"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[920px] rounded-[20px] border border-slate-200 bg-white p-6 shadow-2xl dark:border-[#334155] dark:bg-[#0f172a]"
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="m-0 text-[15px] font-bold text-slate-900 dark:text-slate-100">{previewVideoEvent.category}</h3>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-500">
                  {previewVideoEvent.location}
                  {previewVideoEvent.capture_time
                    ? ` · ${formatDate(previewVideoEvent.capture_time)} ${formatTime(previewVideoEvent.capture_time)}`
                    : ""}
                </p>
              </div>
            </div>
            <div className="flex min-h-[280px] items-center justify-center overflow-hidden border border-slate-200 bg-black dark:border-[#334155]">
              {previewVideoEvent.video_link ? (
                <video
                  src={previewVideoEvent.video_link}
                  controls
                  autoPlay
                  className="max-h-[70vh] w-full object-contain"
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="text-center text-slate-300">
                  <p className="text-[13px]">Video unavailable</p>
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setPreviewVideoEvent(null)}
                className="min-w-[80px] flex justify-center items-center cursor-pointer rounded-[10px] border border-slate-300 bg-slate-100 py-2 px-3 text-[13px] text-slate-700 hover:bg-slate-200 dark:border-[#334155] dark:bg-slate-800/80 dark:text-slate-400 dark:hover:bg-slate-700/80"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
